import { useUser } from "@/features/auth/store";
import { useMyPermissions } from "@/features/permissions/hooks/usePermissionsQueries";
import { isSuperAdmin, isSubAdmin } from "@/lib/auth-utils";

export function useUserPermissions() {
  const user = useUser();
  const { data: myPermissions } = useMyPermissions();
  const permissions = new Set(myPermissions?.permissions ?? []);

  const superAdmin = isSuperAdmin(user);
  const subAdmin = isSubAdmin(user);

  // Only SUPER_ADMIN bypasses the check. SUB_ADMIN is governed by the permissions panel like any
  // other non-owner role, which is the whole point of that panel.
  const has = (permission: string) => superAdmin || permissions.has(permission);

  // 🔴 view-dashboard IS UI-SCOPE ONLY. It opens the route shell and shows menu entries; it closes
  // no endpoint and unlocks no data (imof-backend src/auth/permissions.ts says so at the catalogue
  // entry itself, and the 2026-08-24 audit probed it). Anything that RENDERS DATA must be gated on
  // the permission the endpoint behind it actually checks — otherwise the screen mounts, its
  // queries 403, and the user is shown an error where a page should be.
  const canAccessModule = has("system:view-dashboard");
  const canCreate = has("users:create");
  const canEdit = has("users:update");
  const canDeactivate = has("users:deactivate");
  const canAssignRole = has("users:assign-roles");
  const canViewActivity = has("system:view-audit-logs");
  // Guards /reports/* — every stat, the content widget, and recent logins.
  const canViewAnalytics = has("system:view-analytics");
  // The FAQ screen. ONE global permission, exactly like obs:manage — an FAQ answer is
  // ministry-wide published copy, so there is no department tier to resolve at.
  const canManageFaqs = has("faq:manage");
  const canManagePermissions = has("users:manage-permissions");
  // Delegated to a Sub-Admin: edit the permission overrides of Editors only.
  const canManageEditorPermissions = has("users:manage-editor-permissions");
  const canManageBackups = has("system:backup-restore");
  const canManageSettings = has("system:manage-settings");
  const canAccessSupport = has("support:access");

  return {
    has,
    canAccessModule,
    canCreate,
    canEdit,
    canDeactivate,
    canAssignRole,
    canViewActivity,
    canViewAnalytics,
    canManageFaqs,
    canManagePermissions,
    canManageEditorPermissions,
    canManageBackups,
    canManageSettings,
    canAccessSupport,
    isSuperAdmin: superAdmin,
    isSubAdmin: subAdmin,
  };
}
